import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import { toastErrorMessage } from "../../../utils/toastMessage/toastMessage";
import {
  createCategoryAdminService,
  createSubCategoryAdminService,
  deleteCategoryAdminService,
  deleteSubCategoryAdminService,
  editCategoryAdminService,
  editSubCategoryAdminService,
  getCategoriesAdminService,
  getSubCategoriesAdminService,
} from "../../../services/adminServices";

export const categoryAdminContext = createContext({
  title: "",
  setTitle: () => {},
  image: "",
  setImage: () => {},
  icon: "",
  setIcon: () => {},
  categoryId: "",
  setCategoryId: () => {},
  categories: [],
  setCategories: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  showModal: () => {},
  closeModal: () => {},
  // Start Category
  handleCreateCategory: () => {},
  handleGetCategories: () => {},
  handleEditCategory: () => {},
  handleChangeStatusCategory: () => {},
  handleDeleteCategory: () => {},
  // End Category
  // Start Sub Category
  subCategories: [],
  setSubCategories: () => {},
  handleCreateSubCategory: () => {},
  handleGetSubCategories: () => {},
  handleEditSubCategory: () => {},
  handleChangeStatusSubCategory: () => {},
  handleDeleteSubCategory: () => {},
  // End Sub Category
});

const CategoryAdminContext = ({ children }) => {
  const dispatch = useDispatch();

  const [title, setTitle] = useState("");
  const [image, setImage] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [isShowModal, setIsShowModal] = useState(false);

  //* Start Category
  // Start Modal
  const showModal = (val) => {
    setIsShowModal(true);

    if (val) {
      setCategoryId(val._id);
      setTitle(val.title);
      setImage(val.image);
    }
  };

  const closeModal = () => {
    setIsShowModal(false);

    setCategoryId("");
    setTitle("");
    setImage("");
  };

  // Handle Create Category
  const handleCreateCategory = async (event) => {
    event?.preventDefault();

    const objData = {
      image,
      title,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createCategoryAdminService(objData);
      if (status === 200) {
        closeModal();
        handleGetCategories();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Categories
  const handleGetCategories = async (event) => {
    event?.preventDefault();

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCategoriesAdminService();
      if (status === 200) {
        setCategories(data?.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Category
  const handleEditCategory = async (event) => {
    event?.preventDefault();

    const objData = {
      id: categoryId,
      image,
      title,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editCategoryAdminService(objData);
      if (status === 200) {
        closeModal();
        handleGetCategories();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Category
  const handleDeleteCategory = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteCategoryAdminService(id);
      if (status === 200) {
        handleGetCategories();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };
  //* End Category

  //* Start Sub Category
  // Handle Create Sub Category
  const handleCreateSubCategory = async (event, successFunc) => {
    event?.preventDefault();

    const objData = {
      categoryId,
      image,
      title,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createSubCategoryAdminService(objData);
      if (status === 200) {
        handleGetSubCategories(categoryId);

        if (successFunc) successFunc();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Sub Categories
  const handleGetSubCategories = async (id, successFunc) => {
    const objData = {
      categoryId: id,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getSubCategoriesAdminService(objData);
      if (status === 200) {
        setSubCategories(data.result);
        if (successFunc) successFunc();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Sub Category
  const handleEditSubCategory = async (event, id, successFunc) => {
    event?.preventDefault();

    const objData = {
      id,
      categoryId,
      image,
      title,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editSubCategoryAdminService(objData);
      if (status === 200) {
        handleGetSubCategories(categoryId);
        if (successFunc) successFunc();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Sub Category
  const handleDeleteSubCategory = async (subCatId) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteSubCategoryAdminService(subCatId);
      if (status === 200) {
        handleGetSubCategories(categoryId);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };
  //* End Sub Category

  return (
    <>
      <categoryAdminContext.Provider
        value={{
          categoryId,
          setCategoryId,
          title,
          setTitle,
          image,
          setImage,
          categories,
          setCategories,
          isShowModal,
          setIsShowModal,
          // Start Category
          showModal,
          closeModal,
          handleCreateCategory,
          handleGetCategories,
          handleEditCategory,
          handleDeleteCategory,
          // End Category
          // Start Sub Category
          subCategories,
          setSubCategories,
          handleCreateSubCategory,
          handleGetSubCategories,
          handleEditSubCategory,
          handleDeleteSubCategory,
          // End Sub Category
        }}
      >
        {children}
      </categoryAdminContext.Provider>
    </>
  );
};

export default CategoryAdminContext;
